import React, { useCallback, useState, useRef } from "react";
import CommonButtons from "src/shared/components/CommonButtons";
import { useUser } from "src/shared/context/UserContext";
import { DailySummeryPrint } from "src/print-configuration/printBaseCode";
import useCustomerSummary from "./hooks/useCustomerSummary";
import { LedgerCustomerDetails, LedgerDetails, LedgerOrderItem } from "../types";

interface CustomerSummaryProps {
  customerId: string;
}

function CustomerSummary({ customerId }: CustomerSummaryProps) {
  const { summery, loading, createReport: fetchCustomerSummary } = useCustomerSummary();
  const [showModal, setShowModal] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateError, setDateError] = useState("");
  const { user } = useUser();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const copies = 1;

  const today = new Date().toISOString().split("T")[0];

  const getSixMonthsAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split("T")[0];
  };
  const sixMonthsAgo = getSixMonthsAgo();

  const formatNumber = (val: any) =>
    (val ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ✅ Date shows only dd/mm (no year)
  const formatShortDate = (dateStr: any) => {
    if (!dateStr) return "";
    const raw = typeof dateStr === "object" && dateStr?.$date ? dateStr.$date : dateStr;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  };

  const formatDate = (dateStr: any) => {
    if (!dateStr) return "";
    const raw = typeof dateStr === "object" && dateStr?.$date ? dateStr.$date : dateStr;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setDateError("");
    if (toDate && value && toDate < value) {
      setToDate("");
    }
  };

  const handleToDateChange = (value: string) => {
    if (fromDate && value < fromDate) {
      setDateError("To Date cannot be before From Date");
      return;
    }
    if (value > today) {
      setDateError("To Date cannot be in the future");
      return;
    }
    if (fromDate && value) {
      const from = new Date(fromDate);
      const to = new Date(value);
      const diffMonths =
        (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
      if (diffMonths > 6) {
        setDateError("Date range cannot exceed 6 months");
        return;
      }
    }
    setDateError("");
    setToDate(value);
  };

  const buildCustomerSummaryContent = (report: LedgerDetails) => {
    const ledger = report?.ledger;
    const customerDetails: LedgerCustomerDetails | undefined = ledger?.customerDetails;
    const orders: LedgerOrderItem[] = ledger?.orderDetails ?? [];

    const customerName = customerDetails
      ? `${customerDetails.firstName || ""} ${customerDetails.middleName || ""} ${customerDetails.lastName || ""}`.trim()
      : "";

    const customerPhone =
      customerDetails?.phones?.find((p) => p.isPrimary)?.phone ||
      customerDetails?.phones?.[0]?.phone ||
      "";

    const formatPrintDate = (d: Date) => {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    };

    // ✅ Compact status codes (2–3 chars)
    const formatStatus = (status?: string | null): string => {
      if (!status) return "--";
      const raw = String(status).trim().toLowerCase();
      if (!raw || raw === "-") return "--";

      const key = raw.replace(/[_\s-]/g, "");

      const map: Record<string, string> = {
        // Confirmed
        confirm: "CF",
        confirmed: "CF",
        confirmation: "CF",

        // Complete
        complete: "CP",
        completed: "CP",

        // Payment
        paymentapplied: "PA",
        payment: "PA",
        paid: "PA",
        ledgerpage: "LG",
        ledger: "LG",

        // Cancelled
        cancelled: "CN",
        canceled: "CN",
        cancel: "CN",

        // Refund
        refund: "RF",
        refunded: "RF",

        // Pending / Hold
        pending: "PD",
        hold: "HD",
        onhold: "HD",

        // Created
        ordercreated: "CR",
        created: "CR",
        new: "NW",

        // Delivered / Shipped
        delivered: "DL",
        delivery: "DL",
        shipped: "SH",

        // Others
        unpaid: "UP",
        failed: "FL",
        rejected: "RJ",
        processing: "PR",
        active: "AC",
        inactive: "IN",
        closed: "CL",
      };

      if (map[key]) return map[key];

      // Fuzzy fallbacks
      if (key.includes("cancel")) return "CN";
      if (key.includes("refund")) return "RF";
      if (key.includes("payment")) return "PA";
      if (key.includes("ledger")) return "LG";
      if (key.includes("confirm")) return "CF";
      if (key.includes("deliver")) return "DL";
      if (key.includes("pending")) return "PD";
      if (key.includes("create")) return "CR";
      if (key.includes("complete")) return "CP";
      if (key.includes("ship")) return "SH";

      // Fallback: first 2 letters uppercase
      return key.slice(0, 2).toUpperCase() || "--";
    };

    const prevOutstanding = ledger?.previousOutstanding ?? 0;

    const header = `
      <div class="center bold text-lg">${user?.orgName}</div>
      <div class="center">${user?.firstName} ${user?.middleName} ${user?.lastName}</div>
      <div class="center bold">CUSTOMER SUMMARY</div>
      ${customerName ? `<div class="center">Customer: ${customerName}</div>` : ""}
      ${customerPhone ? `<div class="center">Phone: ${customerPhone}</div>` : ""}
      <div class="center">FROM DATE: ${formatDate(fromDate)}</div>
      <div class="center">TO DATE: ${formatDate(toDate)}</div>
      <div class="dashed-line"></div>
    `;

    const footer = `
      <div class="dashed-line"></div>
      <div class="center small">Printed on ${formatPrintDate(new Date())}</div>
    `;

    if (!orders || orders.length === 0) {
      return `
        <div class="receipt">
          <div class="page-header">
            ${header}
            <div class="flex-row">
              <span>PREV. OUTSTANDING</span>
              <span>${formatNumber(prevOutstanding)}</span>
            </div>
            <div class="dashed-line"></div>
          </div>
          <div class="center" style="padding: 20px 0;">No customer orders found</div>
          <div class="page-footer">
            ${footer}
          </div>
        </div>
      `;
    }

    let rowsHtml = "";
    orders.forEach((order: LedgerOrderItem) => {
      const orderAmount = order.orderAmount ?? order.finalAmount ?? 0;
      const receivedAmount = order.receivedAmount ?? 0;
      const remindingAmount = order.outstandingBalance ?? orderAmount - receivedAmount;

      rowsHtml += `
        <tr>
          <td class="td-id">${order.billNumber ?? "-"}</td>
          <td class="td-date">${formatShortDate(order.orderedOn)}</td>
          <td class="td-status">${formatStatus(order.status)}</td>
          <td class="td-amt">${formatNumber(orderAmount)}</td>
          <td class="td-amt">${formatNumber(receivedAmount)}</td>
          <td class="td-amt">${formatNumber(remindingAmount)}</td>
        </tr>
      `;
    });

    const totalOutstandingBalance = ledger?.totalOutstandingBalance ?? 0;

    return `
      <div class="receipt">
        <div class="page-header">
          ${header}
          <div class="flex-row">
            <span>PREV. OUTSTANDING</span>
            <span>${formatNumber(prevOutstanding)}</span>
          </div>
          <div class="dashed-line"></div>
        </div>

        <table class="cust-table">
          <colgroup>
            <col style="width: 12%;" />
            <col style="width: 12%;" />
            <col style="width: 8%;" />
            <col style="width: 22.66%;" />
            <col style="width: 22.66%;" />
            <col style="width: 22.68%;" />
          </colgroup>

          <thead>
            <tr>
              <th class="td-id">BILL No.</th>
              <th class="td-date">DATE</th>
              <th class="td-status">ST</th>
              <th class="td-amt">AMOUNT</th>
              <th class="td-amt">RECVD</th>
              <th class="td-amt">BAL.</th>
            </tr>
          </thead>

          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="dashed-line"></div>

        <div class="flex-row bold">
          <span>TOTAL OUTSTANDING</span>
          <span>${formatNumber(totalOutstandingBalance)}</span>
        </div>

        <div class="page-footer">
          ${footer}
        </div>
      </div>
    `;
  };

  // ---- Custom CSS (invariant print format, no status badge) ----
  const customerSummaryStyles = `
    <style>
      @page { size: auto; margin: 4mm 3mm 6mm 3mm; }
      * { box-sizing: border-box; }
      html, body {
        margin: 0; padding: 0;
        font-family: 'Courier New', monospace;
        font-size: 10px; color: #000;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .receipt {
        font-family: 'Courier New', monospace;
        font-size: 10px; color: #000;
        width: 100%; padding: 2px;
      }
      .center { text-align: center; }
      .bold { font-weight: bold; }
      .text-lg { font-size: 14px; }
      .small { font-size: 8px; }
      .dashed-line { border-top: 1px dashed #000; margin: 4px 0; }
      .page-header { page-break-after: avoid; break-after: avoid; }
      .page-footer { page-break-before: avoid; break-before: avoid; }

      table.cust-table {
        width: 100%; border-collapse: collapse; border-spacing: 0;
        table-layout: fixed; margin: 4px 0;
        font-family: 'Courier New', monospace;
      }
      table.cust-table thead { display: table-header-group; }
      table.cust-table tfoot { display: table-footer-group; }
      table.cust-table th,
      table.cust-table td {
        padding: 3px 1px;
        font-family: 'Courier New', monospace;
        font-size: 9px; line-height: 1.2;
        vertical-align: middle;
        border: 1px solid #000;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        overflow: visible;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      table.cust-table thead th {
        background: #f0f0f0; font-weight: bold;
        text-align: left; font-size: 8px;
        page-break-after: avoid; break-after: avoid;
      }
      table.cust-table tfoot td {
        border-top: 1px solid #000; font-weight: bold;
        background: #fafafa;
        page-break-before: avoid; break-before: avoid;
      }
      table.cust-table tbody tr { page-break-inside: avoid; break-inside: avoid; }

      .td-id { width: 12%; text-align: center; }
      .td-date { width: 12%; text-align: center; }
      .td-status { width: 8%; text-align: center; }
      .td-amt { width: 22.66%; text-align: right; }
      .td-bold { font-weight: bold; }
      .td-totals { text-align: left; font-weight: bold; }
      .flex-row { display: flex; justify-content: space-between; font-size: 10px; padding: 1px 0; }
      .copy-break { page-break-after: always; break-after: page; }

      @media print {
        .receipt { padding: 0; }
        table.cust-table th, table.cust-table td { border: 1px solid #000 !important; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        tr { page-break-inside: avoid; }
      }
    </style>
  `;

  const buildPrintData = (report: LedgerDetails) => `
    ${DailySummeryPrint}
    ${customerSummaryStyles}
    <body>
      ${Array.from({ length: copies })
        .map(
          (_, index) => `
          <div class="page ${index < copies - 1 ? "copy-break" : ""}">
            ${buildCustomerSummaryContent(report)}
          </div>
        `,
        )
        .join("")}
    </body>
    </html>
  `;

  const printInSameTab = (html: string) => {
    if (iframeRef.current) {
      document.body.removeChild(iframeRef.current);
      iframeRef.current = null;
    }
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    iframeRef.current = iframe;
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };
    if (iframe.contentWindow) {
      iframe.contentWindow.onafterprint = () => {
        if (iframeRef.current) {
          document.body.removeChild(iframeRef.current);
          iframeRef.current = null;
        }
      };
    }
    setTimeout(() => {
      if (iframeRef.current) {
        document.body.removeChild(iframeRef.current);
        iframeRef.current = null;
      }
    }, 8000);
  };

  const createReport = useCallback(async () => {
    if (!customerId) {
      setDateError("Customer ID is required");
      return;
    }
    if (!fromDate || !toDate) {
      setDateError("Please select both From and To dates");
      return;
    }
    if (toDate < fromDate) {
      setDateError("To Date cannot be before From Date");
      return;
    }
    if (toDate > today) {
      setDateError("To Date cannot be in the future");
      return;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffMonths =
      (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (diffMonths > 6) {
      setDateError("Date range cannot exceed 6 months");
      return;
    }

    const result = await fetchCustomerSummary(customerId, fromDate, toDate);
    const report: LedgerDetails | null = (result ?? summery) as LedgerDetails | null;

    if (!report) {
      console.error("No report data available to print");
      return;
    }

    const html = buildPrintData(report);
    printInSameTab(html);

    setShowModal(false);
    setFromDate("");
    setToDate("");
  }, [fromDate, toDate, customerId, summery, today]);

  const handleCloseModal = () => {
    setShowModal(false);
    setDateError("");
    setFromDate("");
    setToDate("");
  };

  return (
    <>
      <CommonButtons
        label={"🖨️ Customer Summary"}
        onClick={() => setShowModal(true)}
        disabled={loading}
        className="rounded-lg px-4 py-2"
      />
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              padding: "20px",
              width: "340px",
              maxWidth: "90%",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "8px" }}>Customer Summary</h3>
            <p style={{ fontSize: "12px", color: "#666", marginBottom: "12px" }}>
              ⚠️ Date range limited to 6 months
            </p>

            <label style={{ display: "block", fontSize: "13px", fontWeight: "500", marginBottom: "4px" }}>
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              min={sixMonthsAgo}
              max={today}
              onChange={(e) => handleFromDateChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "12px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />

            <label style={{ display: "block", fontSize: "13px", fontWeight: "500", marginBottom: "4px" }}>
              To Date
            </label>
            <input
              type="date"
              value={toDate}
              min={fromDate || sixMonthsAgo}
              max={today}
              onChange={(e) => handleToDateChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "8px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />

            {dateError && (
              <p style={{ color: "#dc2626", fontSize: "12px", marginTop: 0, marginBottom: "12px" }}>
                {dateError}
              </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
              <button
                onClick={handleCloseModal}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#e5e7eb",
                  color: "#374151",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={createReport}
                disabled={loading}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? "Loading..." : "Print"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CustomerSummary;